// ============================================================================
// Fleet compliance — chip level derived from a *_exp date vs today.
// DERIVED, never stored (design D2). Principle: track reality, surface gaps as
// chips — never block. green current / amber <=30d / red expired / red missing.
// Status badges only: grey inactive — a sold, retired, inactive or disposed
// vehicle, or a retired machine, is finished, not broken (item 530).
// ============================================================================

export type ComplianceLevel = 'current' | 'amber' | 'expired' | 'missing' | 'inactive';

export interface ComplianceChip {
  level: ComplianceLevel;
  /** short label e.g. "Current", "12d left", "Expired", "Missing" */
  label: string;
  /** days until expiry (negative = past); null when no date */
  days: number | null;
}

const MS_DAY = 86_400_000;

/** Whole days from today (UTC-safe on date-only strings) to the given date. */
function daysUntil(exp: string | null | undefined): number | null {
  if (!exp) return null;
  const d = new Date(exp + (exp.length === 10 ? 'T00:00:00' : ''));
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / MS_DAY);
}

/** Derive the compliance chip for a single expiry date. */
export function complianceChip(exp: string | null | undefined): ComplianceChip {
  const days = daysUntil(exp);
  if (days === null) return { level: 'missing', label: 'Missing', days: null };
  if (days < 0) return { level: 'expired', label: 'Expired', days };
  if (days <= 30) return { level: 'amber', label: `${days}d left`, days };
  return { level: 'current', label: 'Current', days };
}

/** Tailwind classes for a chip level (stone/RAG palette used app-wide). */
export function chipClasses(level: ComplianceLevel): string {
  switch (level) {
    case 'current':
      return 'bg-st-ok-bg text-st-ok-fg border-st-ok-border';
    case 'amber':
      return 'bg-st-warn-bg text-st-warn-fg-strong border-st-warn-border';
    case 'expired':
    case 'missing':
      return 'bg-st-danger-bg text-st-danger-fg border-st-danger-border';
    case 'inactive':
      // Gone from the fleet, not broken. Reuses the existing neutral pill
      // (facility-detail's "Scheduled" badge uses the same three classes) so a
      // retired machine or a sold/retired vehicle doesn't wear the same red as
      // a broken one (item #530).
      return 'bg-tcs-bg-muted text-tcs-text-secondary border-tcs-border';
  }
}

/** True when a chip level represents a gap worth counting in the rollup. */
export function isComplianceGap(level: ComplianceLevel): boolean {
  return level === 'expired' || level === 'missing' || level === 'amber';
}
